/**
 * Per-table match scoreboard for Live Training match / challenge blocks.
 * Games to 11, two clear points; the match is won at ceil(bestOf / 2) games.
 */

export const GAME_POINTS = 11;

function playerRef(player) {
  if (!player) return null;
  const playerId = player.playerId ?? player.player_id ?? null;
  const userId = player.userId ?? player.user_id ?? null;
  if (playerId == null && userId == null) return null;
  return {
    playerId: playerId != null ? String(playerId) : null,
    userId: userId != null ? String(userId) : null,
    name: player.name || "",
  };
}

function samePlayer(a, b) {
  if (!a || !b) return !a && !b;
  if (a.playerId && b.playerId) return a.playerId === b.playerId;
  return Boolean(a.userId && a.userId === b.userId);
}

function emptyTable(tableNumber, playerA, playerB) {
  return { tableNumber, playerA, playerB, sets: [], current: [0, 0] };
}

/**
 * Align the scoreboard with the current seating. Scores are kept for a table
 * while the same two players stay on it.
 */
export function syncScoreboardTables(tables = [], seatedTables = []) {
  const byNumber = new Map(tables.map((t) => [Number(t.tableNumber), t]));
  return seatedTables
    .map((row) => {
      const tableNumber = Number(row.tableNumber ?? row.table_number);
      if (!Number.isFinite(tableNumber)) return null;
      const playerA = playerRef(row.player1);
      const playerB = playerRef(row.player2);
      if (!playerA && !playerB) return null;
      const prev = byNumber.get(tableNumber);
      if (prev && samePlayer(prev.playerA, playerA) && samePlayer(prev.playerB, playerB)) {
        return { ...prev, playerA, playerB };
      }
      return emptyTable(tableNumber, playerA, playerB);
    })
    .filter(Boolean)
    .sort((a, b) => a.tableNumber - b.tableNumber);
}

export function gamesWon(table) {
  const out = [0, 0];
  for (const [a, b] of table?.sets || []) {
    if (a > b) out[0] += 1;
    else if (b > a) out[1] += 1;
  }
  return out;
}

export function gamesToWin(bestOf) {
  return Math.floor((Number(bestOf) || 1) / 2) + 1;
}

/** 0 = side A, 1 = side B, null = match still open. */
export function matchWinnerSide(table, bestOf) {
  const [a, b] = gamesWon(table);
  const need = gamesToWin(bestOf);
  if (a >= need) return 0;
  if (b >= need) return 1;
  return null;
}

/** Game is over at 11+ with a two-point lead. */
export function isGameOver([a, b]) {
  return (a >= GAME_POINTS || b >= GAME_POINTS) && Math.abs(a - b) >= 2;
}

export function addPoint(table, side, delta, bestOf) {
  if (!table || matchWinnerSide(table, bestOf) != null) return table;
  const current = [...table.current];
  current[side] = Math.max(0, Math.min(99, current[side] + delta));
  return { ...table, current };
}

/** Close the current game into the sets list (ignored at 0-0 or after the match ends). */
export function closeGame(table, bestOf) {
  if (!table || matchWinnerSide(table, bestOf) != null) return table;
  const [a, b] = table.current;
  if (a === b) return table;
  return { ...table, sets: [...table.sets, [a, b]], current: [0, 0] };
}

export function undoLastGame(table) {
  if (!table?.sets?.length) return table;
  const sets = table.sets.slice(0, -1);
  return { ...table, sets, current: table.sets[table.sets.length - 1] };
}

export function resetTableScore(table) {
  if (!table) return table;
  return { ...table, sets: [], current: [0, 0] };
}

export function scoreboardHasScores(tables = []) {
  return tables.some(
    (t) => t.sets.length > 0 || t.current[0] > 0 || t.current[1] > 0
  );
}
